import type { ValueChainEdge, ValueChainNode } from '@/types/valueChain';

export const NODE_WIDTH = 210;
export const NODE_HEIGHT = 58;
export const VERTICAL_GAP = 76;
export const HORIZONTAL_GAP = 32;

export type NodePosition = { x: number; y: number };

export type LaidOutEdge = {
  id: string;
  from: string;
  to: string;
  relation: ValueChainEdge['relation'];
  supplyChain?: boolean;
  start: { x: number; y: number };
  end: { x: number; y: number };
  label: { x: number; y: number };
};

export type TreeLayout = {
  positions: Record<string, NodePosition>;
  edges: LaidOutEdge[];
  width: number;
  height: number;
};

function childrenOf(
  nodeId: string,
  edges: ValueChainEdge[],
  visibleIds: Set<string>,
): ValueChainEdge[] {
  return edges.filter(
    (edge) => edge.from === nodeId && visibleIds.has(edge.to) && !edge.supplyChain,
  );
}

function subtreeWidth(
  nodeId: string,
  edges: ValueChainEdge[],
  visibleIds: Set<string>,
  memo: Map<string, number>,
): number {
  const cached = memo.get(nodeId);
  if (cached != null) return cached;
  const kids = childrenOf(nodeId, edges, visibleIds);
  if (kids.length === 0) {
    memo.set(nodeId, NODE_WIDTH);
    return NODE_WIDTH;
  }
  const width = Math.max(
    NODE_WIDTH,
    kids.reduce((sum, edge, index) => {
      const childWidth = subtreeWidth(edge.to, edges, visibleIds, memo);
      return sum + childWidth + (index === 0 ? 0 : HORIZONTAL_GAP);
    }, 0),
  );
  memo.set(nodeId, width);
  return width;
}

function place(
  nodeId: string,
  left: number,
  top: number,
  edges: ValueChainEdge[],
  visibleIds: Set<string>,
  widths: Map<string, number>,
  positions: Record<string, NodePosition>,
) {
  const width = widths.get(nodeId) ?? NODE_WIDTH;
  positions[nodeId] = { x: left + (width - NODE_WIDTH) / 2, y: top };
  const kids = childrenOf(nodeId, edges, visibleIds);
  let cursor = left;
  const childTop = top + NODE_HEIGHT + VERTICAL_GAP;
  kids.forEach((edge) => {
    const childWidth = widths.get(edge.to) ?? NODE_WIDTH;
    place(edge.to, cursor, childTop, edges, visibleIds, widths, positions);
    cursor += childWidth + HORIZONTAL_GAP;
  });
}

function layoutSupplyChain(
  edges: ValueChainEdge[],
  visibleIds: Set<string>,
  positions: Record<string, NodePosition>,
  treeWidth: number,
) {
  const supplyEdges = edges.filter(
    (edge) =>
      edge.supplyChain &&
      visibleIds.has(edge.from) &&
      visibleIds.has(edge.to) &&
      positions[edge.from],
  );
  const companyIds = [...new Set(supplyEdges.map((edge) => edge.to))];
  const originX = treeWidth + 88;
  companyIds.forEach((id, index) => {
    if (positions[id]) return;
    const related = supplyEdges.filter((edge) => edge.to === id);
    const avgY =
      related.reduce((sum, edge) => sum + (positions[edge.from]?.y ?? 0), 0) /
      Math.max(related.length, 1);
    positions[id] = { x: originX, y: avgY + index * (NODE_HEIGHT + 28) };
  });
}

export function layoutValueChainTree(
  rootId: string,
  nodes: ValueChainNode[],
  edges: ValueChainEdge[],
  visibleIds: Set<string>,
  showSupplyChain: boolean,
): TreeLayout {
  const positions: Record<string, NodePosition> = {};
  const widths = new Map<string, number>();
  if (visibleIds.has(rootId)) {
    subtreeWidth(rootId, edges, visibleIds, widths);
    place(rootId, 0, 0, edges, visibleIds, widths, positions);
  } else {
    let y = 0;
    nodes.forEach((node) => {
      if (!visibleIds.has(node.id) || positions[node.id]) return;
      positions[node.id] = { x: 0, y };
      y += NODE_HEIGHT + VERTICAL_GAP;
    });
  }

  const treeWidth = Math.max(NODE_WIDTH, ...Object.values(positions).map((p) => p.x + NODE_WIDTH), 0);
  if (showSupplyChain) {
    layoutSupplyChain(edges, visibleIds, positions, treeWidth);
  }

  const laidEdges: LaidOutEdge[] = edges
    .filter((edge) => {
      if (!visibleIds.has(edge.from) || !visibleIds.has(edge.to)) return false;
      if (edge.supplyChain && !showSupplyChain) return false;
      return Boolean(positions[edge.from] && positions[edge.to]);
    })
    .map((edge) => {
      const from = positions[edge.from];
      const to = positions[edge.to];
      const start = { x: from.x + NODE_WIDTH / 2, y: from.y + NODE_HEIGHT };
      const end = { x: to.x + NODE_WIDTH / 2, y: to.y };
      return {
        id: edge.id,
        from: edge.from,
        to: edge.to,
        relation: edge.relation,
        supplyChain: edge.supplyChain,
        start,
        end,
        label: {
          x: (start.x + end.x) / 2,
          y: (start.y + end.y) / 2,
        },
      };
    });

  const xs = Object.values(positions).map((p) => p.x);
  const ys = Object.values(positions).map((p) => p.y);
  const minX = Math.min(0, ...xs);
  const minY = Math.min(0, ...ys);
  if (minX !== 0 || minY !== 0) {
    Object.values(positions).forEach((p) => {
      p.x -= minX;
      p.y -= minY;
    });
    laidEdges.forEach((edge) => {
      edge.start.x -= minX;
      edge.start.y -= minY;
      edge.end.x -= minX;
      edge.end.y -= minY;
      edge.label.x -= minX;
      edge.label.y -= minY;
    });
  }

  const width =
    Math.max(NODE_WIDTH, ...Object.values(positions).map((p) => p.x + NODE_WIDTH)) + 48;
  const height =
    Math.max(NODE_HEIGHT, ...Object.values(positions).map((p) => p.y + NODE_HEIGHT)) + 48;

  return { positions, edges: laidEdges, width, height };
}

export function edgePath(start: { x: number; y: number }, end: { x: number; y: number }): string {
  const midY = start.y + (end.y - start.y) * 0.45;
  return `M ${start.x} ${start.y} C ${start.x} ${midY}, ${end.x} ${midY}, ${end.x} ${end.y}`;
}
